/**
 * SOLOMON — ConfirmCostDialog
 *
 * Modal that fires when operator clicks a button whose static
 * cost_non_cached_usd_max > threshold (default $2.00).
 *
 * On open, fetches GET /api/system/lead-click-cost/:leadId/:buttonId
 * to get the actual cost for THIS lead's current cache state. Shows
 * the cost + cache-state callout per the design.
 *
 * Falls back to worst-case from the static map if the per-lead lookup
 * fails (annotated with a footer note).
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCostMap } from '../hooks/useCostMap.js';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function ConfirmCostDialog({ isOpen, buttonId, leadId, onConfirm, onCancel, estimate = null }) {
  const { authFetch } = useAuth();
  const { costMap } = useCostMap();
  const [leadCost, setLeadCost] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setLeadCost(null);
      setLoadFailed(false);
      return;
    }
    if (estimate) {
      // Caller supplied a live server-computed estimate (e.g. the lead-pull
      // dry-run) — no per-lead lookup, no static-map fallback needed.
      return;
    }
    if (!leadId) {
      // Search has no lead context — use static map's worst case
      setLoadFailed(true);
      return;
    }

    let mounted = true;
    setPending(true);
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/api/system/lead-click-cost/${leadId}/${buttonId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'lookup failed');
        if (mounted) setLeadCost(json.data);
      } catch (err) {
        console.error('[ConfirmCostDialog] per-lead lookup failed:', err);
        if (mounted) setLoadFailed(true);
      } finally {
        if (mounted) setPending(false);
      }
    })();
    return () => { mounted = false; };
  }, [isOpen, leadId, buttonId, estimate]);

  if (!isOpen || !costMap) return null;

  const cfg = costMap.buttons[buttonId];
  if (!cfg) return null;

  const displayCostLabel = estimate
    ? estimate.costLabel
    : leadCost
      ? leadCost.expected_cost_label
      : `~$${cfg.cost_non_cached_usd_max.toFixed(2)} (worst case)`;
  const displayCacheState = estimate
    ? (estimate.note || '')
    : (leadCost?.cache_state_label || '');
  const fallbackNote = !estimate && loadFailed
    ? 'Cache state unavailable — showing worst-case.'
    : '';

  return (
    <div className="confirm-cost-dialog-backdrop" onClick={onCancel}>
      <div className="confirm-cost-dialog" onClick={e => e.stopPropagation()}>
        <h3 className="dialog-title">Confirm {cfg.label}</h3>
        <div className="dialog-body">
          This click will cost
          <div className="dialog-cost-line">
            {pending ? '…' : displayCostLabel}
          </div>
          {displayCacheState && (
            <div className="dialog-cache-state">{displayCacheState}</div>
          )}
          {fallbackNote && (
            <div className="dialog-fallback-note">{fallbackNote}</div>
          )}
        </div>
        <div className="dialog-actions">
          <button className="dialog-btn-cancel" onClick={onCancel}>Cancel</button>
          <button className="dialog-btn-confirm" onClick={onConfirm} disabled={pending}>
            Confirm — {cfg.label}
          </button>
        </div>
      </div>
    </div>
  );
}
